import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { providersAPI, apiMappingsAPI } from '../../services/api';
import type { Provider, SKUMappingRow, RegionMappingRow, SQLLicenseRateRow } from '../../types';

const APIMappings: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [skuMappings, setSkuMappings] = useState<SKUMappingRow[]>([]);
  const [regionMappings, setRegionMappings] = useState<RegionMappingRow[]>([]);
  const [sqlRates, setSqlRates] = useState<SQLLicenseRateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Track original values for change detection
  const [originalSkuMap, setOriginalSkuMap] = useState<Map<number, string | null>>(new Map());
  const [originalRegionMap, setOriginalRegionMap] = useState<Map<number, string | null>>(new Map());
  const [originalSqlRateMap, setOriginalSqlRateMap] = useState<Map<number, number | null>>(new Map());

  useEffect(() => {
    providersAPI.getAll().then(res => {
      const active = (res.data as Provider[]).filter(p => p.IsActive);
      setProviders(active);
    });
  }, []);

  const selectedProvider = providers[selectedTab];

  const fetchMappings = useCallback(async (providerId: number) => {
    setLoading(true);
    setAlert(null);
    try {
      const [skuRes, regionRes, sqlRatesRes] = await Promise.all([
        apiMappingsAPI.getSKUMappings(providerId),
        apiMappingsAPI.getRegionMappings(providerId),
        apiMappingsAPI.getSQLLicenseRates(providerId),
      ]);
      const skuData = skuRes.data as SKUMappingRow[];
      const regionData = regionRes.data as RegionMappingRow[];
      const sqlRatesData = sqlRatesRes.data as SQLLicenseRateRow[];
      setSkuMappings(skuData);
      setRegionMappings(regionData);
      setSqlRates(sqlRatesData);

      // Store originals
      const skuMap = new Map<number, string | null>();
      skuData.forEach(s => skuMap.set(s.VMTypeId, s.ExternalSKU));
      setOriginalSkuMap(skuMap);

      const regMap = new Map<number, string | null>();
      regionData.forEach(r => regMap.set(r.RegionId, r.ExternalRegion));
      setOriginalRegionMap(regMap);

      const rateMap = new Map<number, number | null>();
      sqlRatesData.forEach(r => rateMap.set(r.DatabaseEditionId, r.CostPerVCPU));
      setOriginalSqlRateMap(rateMap);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load mappings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      fetchMappings(selectedProvider.ProviderId);
    }
  }, [selectedProvider, fetchMappings]);

  const handleSkuChange = (vmTypeId: number, value: string) => {
    setSkuMappings(prev =>
      prev.map(s => (s.VMTypeId === vmTypeId ? { ...s, ExternalSKU: value || null } : s))
    );
  };

  const handleRegionChange = (regionId: number, value: string) => {
    setRegionMappings(prev =>
      prev.map(r => (r.RegionId === regionId ? { ...r, ExternalRegion: value || null } : r))
    );
  };

  const handleSqlRateChange = (editionId: number, value: string) => {
    setSqlRates(prev =>
      prev.map(r => (r.DatabaseEditionId === editionId ? { ...r, CostPerVCPU: value === '' ? null : parseFloat(value) } : r))
    );
  };

  const handleSave = async () => {
    if (!selectedProvider) return;
    setSaving(true);
    setAlert(null);

    try {
      // Only send changed SKU mappings
      const changedSkus = skuMappings.filter(
        s => (s.ExternalSKU || null) !== (originalSkuMap.get(s.VMTypeId) || null)
      );
      // Only send changed region mappings
      const changedRegions = regionMappings.filter(
        r => (r.ExternalRegion || null) !== (originalRegionMap.get(r.RegionId) || null)
      );
      // Only send changed SQL license rates
      const changedRates = sqlRates.filter(
        r => (r.CostPerVCPU ?? null) !== (originalSqlRateMap.get(r.DatabaseEditionId) ?? null)
      );

      const promises: Promise<any>[] = [];
      if (changedSkus.length > 0) {
        promises.push(
          apiMappingsAPI.saveSKUMappings(
            selectedProvider.ProviderId,
            changedSkus.map(s => ({ vmTypeId: s.VMTypeId, externalSKU: s.ExternalSKU }))
          )
        );
      }
      if (changedRegions.length > 0) {
        promises.push(
          apiMappingsAPI.saveRegionMappings(
            selectedProvider.ProviderId,
            changedRegions.map(r => ({ regionId: r.RegionId, externalRegion: r.ExternalRegion }))
          )
        );
      }
      if (changedRates.length > 0) {
        promises.push(
          apiMappingsAPI.saveSQLLicenseRates(
            selectedProvider.ProviderId,
            changedRates.map(r => ({ databaseEditionId: r.DatabaseEditionId, costPerVCPU: r.CostPerVCPU }))
          )
        );
      }

      if (promises.length === 0) {
        setAlert({ type: 'success', message: 'No changes to save' });
        setSaving(false);
        return;
      }

      await Promise.all(promises);
      const parts = [];
      if (changedSkus.length > 0) parts.push(`${changedSkus.length} SKU`);
      if (changedRegions.length > 0) parts.push(`${changedRegions.length} region`);
      if (changedRates.length > 0) parts.push(`${changedRates.length} SQL license rate`);
      setAlert({ type: 'success', message: `Saved ${parts.join(', ')} mapping(s)` });

      // Refresh to get updated originals
      await fetchMappings(selectedProvider.ProviderId);
    } catch {
      setAlert({ type: 'error', message: 'Failed to save mappings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        API Mappings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Map internal VM types and regions to external cloud provider SKU names and region codes for automated price import.
      </Typography>

      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {providers.length > 0 && (
        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
          {providers.map(p => (
            <Tab key={p.ProviderId} label={p.ProviderName} />
          ))}
        </Tabs>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Region Mappings */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Region Mappings
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Region Name</TableCell>
                  <TableCell>Region Code</TableCell>
                  <TableCell>External Region</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regionMappings.map(r => (
                  <TableRow key={r.RegionId}>
                    <TableCell>{r.RegionName}</TableCell>
                    <TableCell>{r.RegionCode}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={r.ExternalRegion || ''}
                        onChange={e => handleRegionChange(r.RegionId, e.target.value)}
                        placeholder="e.g. eastus2, us-east-1"
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {regionMappings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No regions found for this provider
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* SKU Mappings */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            VM SKU Mappings
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>VM Type</TableCell>
                  <TableCell>CPU</TableCell>
                  <TableCell>RAM (GB)</TableCell>
                  <TableCell>External SKU</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skuMappings.map(s => (
                  <TableRow key={s.VMTypeId}>
                    <TableCell>{s.VMName}</TableCell>
                    <TableCell>{s.CPU}</TableCell>
                    <TableCell>{s.RAM}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={s.ExternalSKU || ''}
                        onChange={e => handleSkuChange(s.VMTypeId, e.target.value)}
                        placeholder="e.g. Standard_D8s_v6, m5.2xlarge"
                        sx={{ minWidth: 250 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* SQL License Rates */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            SQL License Rates (per vCPU/month)
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Database Type</TableCell>
                  <TableCell>Edition</TableCell>
                  <TableCell>Cost per vCPU</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sqlRates.map(r => (
                  <TableRow key={r.DatabaseEditionId}>
                    <TableCell>{r.DatabaseName}</TableCell>
                    <TableCell>{r.EditionName}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={r.CostPerVCPU ?? ''}
                        onChange={e => handleSqlRateChange(r.DatabaseEditionId, e.target.value)}
                        placeholder="e.g. 73.00"
                        inputProps={{ step: 0.01, min: 0 }}
                        sx={{ width: 130 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {sqlRates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No database editions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ mb: 2 }}
          >
            {saving ? 'Saving...' : 'Save Mappings'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default APIMappings;
